/**
 * CopilotSidebar — Tee Agent co-pilot surface.
 *
 * Two layouts, driven by the store's `layoutMode`:
 *  - "panel": a right-side overlay drawer that floats over the dashboard
 *    (with a dimming scrim when unpinned), collapsible to a thin edge strip.
 *  - "full":  a dedicated full-screen workspace — history rail on the left,
 *    a wide conversation in the centre, live page context on the right.
 *
 * Both share one conversation renderer + composer. Integrates ContextBanner,
 * ChatMessage, VoiceInput, SuggestionChips, QuickActionPanel, ResponseOptions,
 * an inline ApprovalCard for pending agent actions, and a session history list.
 */
import { useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Send,
  Loader2,
  Pin,
  PinOff,
  X,
  RotateCcw,
  History,
  PanelRightClose,
  Maximize2,
  Minimize2,
  ChevronLeft,
  Plus,
  Sparkles,
  MessageSquare,
  Eye,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import logoSvg from "@assets/svgs/logo.svg";
import { useTeeAgentStore } from "@/zustand/useTeeAgentStore";
import { useNotificationStore } from "@/zustand/useNotificationStore";
import { ChatMessage } from "./ChatMessage";
import { ContextBanner } from "./ContextBanner";
import { AgentOrb } from "./AgentOrb";
import { VoiceInput } from "./VoiceInput";
import { SuggestionChips } from "./SuggestionChips";
import { QuickActionPanel } from "./QuickActionPanel";
import { ApprovalCard, type ApprovalField } from "./ApprovalCard";
import { ResponseOptionsList, type ResponseOption } from "./ResponseOptionCard";
import type { QuickAction } from "./QuickActionCard";
import type { ChatSession, AgentAction } from "@/types/teeAgent";
import { cn } from "@/lib/utils";
import {
  sendChatMessage,
  getChatSessionDetail,
  getChatSessions,
  executeAction,
} from "@/services/teeAgentService";
import { getPageContext } from "./pageContextConfig";

/** Humanise an action_type like "create_booking" → "Create booking". */
const humanizeActionType = (actionType: string) => {
  const s = actionType.replace(/[_-]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/** Map a pending AgentAction's parameters into ApprovalCard fields. */
const actionToFields = (action: AgentAction): ApprovalField[] =>
  Object.entries(action.parameters || {}).map(([key, value]) => ({
    key,
    label: key.replace(/[_-]+/g, " "),
    value:
      value === null || value === undefined
        ? "—"
        : typeof value === "object"
          ? JSON.stringify(value)
          : String(value),
    editable: false,
  }));

/** Best-effort relative "time ago" that avoids extra date deps. */
const timeAgo = (iso?: string) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const CopilotSidebar = () => {
  const location = useLocation();
  const {
    isOpen,
    isMinimized,
    isPinned,
    isLoading,
    layoutMode,
    showHistory,
    agentState,
    setIsLoading,
    setIsMinimized,
    setAgentState,
    messages,
    setMessages,
    addMessage,
    sessions,
    setSessions,
    currentSession,
    setCurrentSession,
    pendingAction,
    setPendingAction,
    inputMessage,
    setInputMessage,
    responseOptions,
    setResponseOptions,
    clearResponseOptions,
    screenContext,
    setScreenContext,
    togglePinned,
    toggleOpen,
    toggleLayoutMode,
    setLayoutMode,
    setShowHistory,
    clearChat,
  } = useTeeAgentStore();

  const { addApiErrorNotification } = useNotificationStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const isFull = layoutMode === "full";

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showHistory]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized && !showHistory) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized, showHistory, layoutMode]);

  // Reflect page changes into agent state
  useEffect(() => {
    if (!isLoading && agentState !== "listening") {
      setAgentState("seeing");
    }
  }, [location.pathname]);

  // Update screen context when the route changes so the AI knows the page
  useEffect(() => {
    const ctx = getPageContext(location.pathname);
    setScreenContext({
      pageName: ctx.pageName,
      summary: `User is viewing the ${ctx.pageName} page`,
      chips: ctx.actions.map((a) => a.title),
    });
  }, [location.pathname]);

  // Load session messages if a session is selected but not yet loaded
  useEffect(() => {
    if (currentSession && messages.length === 0) {
      loadSessionMessages(currentSession.id);
    }
  }, [currentSession]);

  // Lazy-load the session list the first time history is opened
  useEffect(() => {
    if (showHistory && sessions.length === 0) {
      loadSessions();
    }
  }, [showHistory]);

  const loadSessions = async () => {
    try {
      const data = await getChatSessions();
      setSessions(data.sessions);
    } catch (error) {
      addApiErrorNotification(error, "Failed to load conversations");
    }
  };

  const loadSessionMessages = async (sessionId: string) => {
    try {
      const data = await getChatSessionDetail(sessionId);
      setMessages(data.messages);
    } catch (error) {
      addApiErrorNotification(error, "Failed to load chat history");
    }
  };

  /** Parse AI response for inline option choices. */
  const parseResponseOptions = (
    content: string,
    metadata?: Record<string, unknown>
  ): ResponseOption[] => {
    if (metadata?.options && Array.isArray(metadata.options)) {
      return (metadata.options as ResponseOption[]).slice(0, 3);
    }
    const optionPattern = /^\d+\.\s*\*\*(.+?)\*\*\s*[-–]\s*(.+)$/gm;
    const matches = [...content.matchAll(optionPattern)];
    if (matches.length > 0) {
      return matches.slice(0, 3).map((match, index) => ({
        id: `option-${index}`,
        title: match[1].trim(),
        description: match[2].trim(),
        value: match[1].trim(),
      }));
    }
    return [];
  };

  const handleSendMessage = async (messageText?: string) => {
    const message = messageText || inputMessage.trim();
    if (!message || isLoading) return;

    setShowHistory(false);
    setInputMessage("");
    clearResponseOptions();
    setIsLoading(true);
    setAgentState("thinking");

    try {
      const response = await sendChatMessage({
        message: message,
        session_id: currentSession?.id,
        screen_context: screenContext || undefined,
      });

      if (!currentSession || currentSession.id !== response.session_id) {
        setCurrentSession({
          id: response.session_id,
          user: 0,
          title: message.substring(0, 50),
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }

      const tempUserMessage = {
        id: `temp-${Date.now()}`,
        session: response.session_id,
        role: "user" as const,
        content: message,
        created_at: new Date().toISOString(),
      };
      addMessage(tempUserMessage);
      addMessage(response.message);

      const options = parseResponseOptions(
        response.message.content,
        response.message.metadata
      );
      if (options.length > 0) {
        setResponseOptions(options);
      }

      if (response.action && response.requires_confirmation) {
        setPendingAction(response.action);
      }

      setAgentState("seeing");
    } catch (error) {
      addApiErrorNotification(error, "Failed to send message");
      setAgentState("seeing");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveAction = async (action: AgentAction) => {
    setIsLoading(true);
    setAgentState("executing");
    try {
      const response = await executeAction({ action_id: action.id, approved: true });
      if (response.message) addMessage(response.message);
      setPendingAction(null);
    } catch (error) {
      addApiErrorNotification(error, "Failed to run action");
    } finally {
      setIsLoading(false);
      setAgentState("seeing");
    }
  };

  const handleRejectAction = async (action: AgentAction) => {
    try {
      await executeAction({ action_id: action.id, approved: false });
    } catch (error) {
      addApiErrorNotification(error, "Failed to dismiss action");
    } finally {
      setPendingAction(null);
    }
  };

  const handleSelectSession = (session: ChatSession) => {
    setCurrentSession(session);
    setMessages([]);
    setShowHistory(false);
    loadSessionMessages(session.id);
  };

  const handleNewConversation = () => {
    clearChat();
    setShowHistory(false);
  };

  const handleQuickActionClick = (action: QuickAction) => handleSendMessage(action.prompt);
  const handleResponseOptionSelect = (option: ResponseOption) => handleSendMessage(option.value);
  const handleVoiceTranscript = (text: string) => handleSendMessage(text);
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Suggestion chips based on page context and input state
  const pageContext = getPageContext(location.pathname);
  const chipActions = useMemo(() => {
    if (inputMessage.trim()) {
      const lower = inputMessage.toLowerCase();
      if (lower.includes("book")) {
        return [
          { label: "Book for today", prompt: "Book for today" },
          { label: "Book Bay 3", prompt: "Book Bay 3" },
          { label: "Show availability", prompt: "Show availability" },
        ];
      }
      if (lower.includes("customer") || lower.includes("find")) {
        return [
          { label: "Search by name", prompt: "Search by name" },
          { label: "Search by email", prompt: "Search by email" },
          { label: "Show VIP customers", prompt: "Show VIP customers" },
        ];
      }
      return [];
    }
    if (messages.length === 0 && pageContext.actions.length > 0) {
      return pageContext.actions.map((a) => ({
        label: a.title,
        prompt: a.prompt.includes(":")
          ? a.prompt.split(":").slice(1).join(":").trim()
          : a.prompt,
      }));
    }
    return [];
  }, [inputMessage, messages.length, pageContext]);

  const suggestionChips = chipActions.map((c) => c.label);
  const chipPromptMap = useMemo(
    () => Object.fromEntries(chipActions.map((c) => [c.label, c.prompt])),
    [chipActions]
  );

  if (!isOpen) return null;

  const statusLabel =
    agentState === "listening"
      ? "Listening…"
      : agentState === "thinking"
        ? "Thinking…"
        : agentState === "executing"
          ? "Working…"
          : "Watching your screen";

  /* --------------------------------------------------------------------- */
  /* Collapsed edge strip                                                   */
  /* --------------------------------------------------------------------- */
  if (isMinimized && !isFull) {
    return (
      <div
        className={cn(
          "flex flex-col items-center gap-4 py-4 bg-white z-40",
          isPinned
            ? "relative w-[56px] shrink-0 border-l border-gray6"
            : "fixed top-0 right-0 bottom-0 w-[56px] shadow-[-8px_0_32px_rgba(32,41,54,0.08)]"
        )}
      >
        <button
          onClick={() => setIsMinimized(false)}
          title="Open Tee Agent"
          className="relative grid h-10 w-10 place-items-center rounded-xl bg-green2 text-white hover:bg-green1 transition-colors"
        >
          <Sparkles className="h-5 w-5" />
          {pendingAction && (
            <span className="absolute -right-1 -top-1 grid h-4 w-4 place-items-center rounded-full bg-red2 text-[9px] font-bold text-white">
              1
            </span>
          )}
        </button>
        <div className="h-px w-6 bg-gray6" />
        <button
          onClick={() => {
            setIsMinimized(false);
            setShowHistory(true);
          }}
          title="History"
          className="text-gray3 hover:text-green2 transition-colors"
        >
          <History className="h-5 w-5" />
        </button>
        <button
          onClick={() => setIsMinimized(false)}
          title="Conversation"
          className="text-gray3 hover:text-green2 transition-colors"
        >
          <MessageSquare className="h-5 w-5" />
        </button>
      </div>
    );
  }

  /* --------------------------------------------------------------------- */
  /* Shared: conversation body                                              */
  /* --------------------------------------------------------------------- */
  const renderConversation = (wide = false) => (
    <div className={cn("flex-1 overflow-y-auto bg-[#FBFCFD]", wide ? "px-6 py-6" : "px-3 py-3")}>
      <div className={cn(wide && "mx-auto max-w-[720px]")}>
        {messages.length === 0 && !pendingAction ? (
          <div className="mt-4 text-center">
            <div className="inline-block">
              <AgentOrb state="seeing" size={44} />
            </div>
            <div className="mt-3 text-[15px] font-bold text-black1">Hi — what can I do?</div>
            <div className="mt-1 text-[12px] px-3 text-gray3">
              Try "Book Bay 3 for Maya at 4pm" or tap the mic.
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4 px-1 text-left">
              {[
                { t: "Book a bay", s: "New booking for a customer", i: "＋" },
                { t: "Find a customer", s: "Search by name or email", i: "⌕" },
                { t: "Today's summary", s: "Bookings, revenue, risks", i: "◔" },
                { t: "Who's at risk?", s: "Flag no-show bookings", i: "⚠" },
              ].map((c) => (
                <div
                  key={c.t}
                  onClick={() => handleSendMessage(c.t)}
                  className="p-2.5 rounded-lg cursor-pointer bg-white border border-gray6 hover:border-greentab hover:shadow-sm transition-all duration-200"
                >
                  <div className="text-[13px] text-green2">{c.i}</div>
                  <div className="text-[12.5px] font-semibold mt-1 text-black1">{c.t}</div>
                  <div className="text-[10.5px] mt-0.5 text-gray3">{c.s}</div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <QuickActionPanel onActionClick={handleQuickActionClick} isLoading={isLoading} />
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div key={message.id}>
                <ChatMessage message={message} onSuggestionClick={handleSendMessage} />
                {message.role === "assistant" &&
                  index === messages.length - 1 &&
                  responseOptions.length > 0 &&
                  !isLoading && (
                    <div className="ml-9 mb-4">
                      <ResponseOptionsList
                        options={responseOptions}
                        onSelect={handleResponseOptionSelect}
                        isLoading={isLoading}
                        title="Choose an option:"
                        variant="compact"
                      />
                    </div>
                  )}
              </div>
            ))}

            {/* Inline approval card for a pending agent action */}
            {pendingAction && (
              <div className="ml-9 mb-4">
                <ApprovalCard
                  title={humanizeActionType(pendingAction.action_type)}
                  fields={actionToFields(pendingAction)}
                  riskLevel="medium"
                  isLoading={isLoading}
                  onApprove={() => handleApproveAction(pendingAction)}
                  onReject={() => handleRejectAction(pendingAction)}
                />
              </div>
            )}

            {isLoading && (
              <div className="flex gap-2 mb-3">
                <div className="pt-1">
                  <AgentOrb state="thinking" size={22} />
                </div>
                <div className="flex-1">
                  <div className="px-3.5 py-2.5 rounded-2xl rounded-tl-sm bg-white border border-gray6 inline-flex items-center gap-2">
                    <div className="flex space-x-1">
                      <div className="w-1.5 h-1.5 bg-green2 rounded-full animate-bounce [animation-delay:-0.3s]" />
                      <div className="w-1.5 h-1.5 bg-green2 rounded-full animate-bounce [animation-delay:-0.15s]" />
                      <div className="w-1.5 h-1.5 bg-green2 rounded-full animate-bounce" />
                    </div>
                    <span className="text-[11px] font-medium text-gray4 ml-1">Thinking…</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
    </div>
  );

  /* --------------------------------------------------------------------- */
  /* Shared: history list                                                   */
  /* --------------------------------------------------------------------- */
  const renderHistory = (wide = false) => (
    <div className={cn("flex-1 overflow-y-auto bg-white", wide ? "px-3 py-3" : "px-2 py-2")}>
      <button
        onClick={handleNewConversation}
        className="mb-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-green2 px-3 py-2 text-[13px] font-semibold text-white hover:bg-green1"
      >
        <Plus className="h-4 w-4" />
        New conversation
      </button>
      {sessions.length === 0 ? (
        <p className="px-2 py-6 text-center text-[12px] text-gray3">No past conversations yet.</p>
      ) : (
        sessions.map((s) => {
          const active = currentSession?.id === s.id;
          return (
            <button
              key={s.id}
              onClick={() => handleSelectSession(s)}
              className={cn(
                "mt-1 flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors",
                active
                  ? "border border-green3/50 bg-green4/50"
                  : "hover:bg-gray7 border border-transparent"
              )}
            >
              <MessageSquare
                className={cn("mt-0.5 h-4 w-4 shrink-0", active ? "text-green2" : "text-gray4")}
              />
              <div className="min-w-0">
                <p className="truncate text-[13px] font-medium text-black1">
                  {s.title || "Untitled conversation"}
                </p>
                <p className="truncate text-[11px] text-gray3">{timeAgo(s.updated_at)}</p>
              </div>
            </button>
          );
        })
      )}
    </div>
  );

  /* --------------------------------------------------------------------- */
  /* Shared: composer                                                       */
  /* --------------------------------------------------------------------- */
  const renderComposer = (wide = false) => (
    <div className={cn("border-t border-gray6 bg-white shrink-0", wide ? "px-6 py-4" : "p-3")}>
      <div className={cn(wide && "mx-auto max-w-[720px]")}>
        <VoiceInput onTranscript={handleVoiceTranscript} className="mb-2" />
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask Tee Agent, or tell it what to do…"
            className={cn(
              "flex-1 resize-none rounded-lg border border-gray5 px-3 py-2 text-[13px]",
              "focus:outline-none focus:ring-2 focus:ring-greentab focus:border-transparent",
              "placeholder:text-gray4"
            )}
            rows={1}
            disabled={isLoading}
          />
          <Button
            onClick={() => handleSendMessage()}
            disabled={!inputMessage.trim() || isLoading}
            className="bg-green2 hover:bg-green1 h-9 w-9 p-0"
            size="icon"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex items-center gap-3 mt-2 text-[10px] text-gray4">
          <span>
            <kbd className="px-1 py-0.5 rounded bg-gray7 font-mono text-[9px]">⏎</kbd> send
          </span>
          <span>
            <kbd className="px-1 py-0.5 rounded bg-gray7 font-mono text-[9px]">⇧⏎</kbd> new line
          </span>
          <span className="ml-auto">Actions always confirm first</span>
        </div>
      </div>
    </div>
  );

  /* --------------------------------------------------------------------- */
  /* Header control buttons (shared)                                        */
  /* --------------------------------------------------------------------- */
  const iconBtn = (
    key: string,
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
    extra?: string
  ) => (
    <TooltipProvider key={key}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClick}
            className={cn("h-7 w-7 text-gray3", extra)}
          >
            {icon}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  /* --------------------------------------------------------------------- */
  /* FULL-SCREEN workspace                                                  */
  /* --------------------------------------------------------------------- */
  if (isFull) {
    return (
      <div className="fixed inset-0 z-50 flex bg-white">
        {/* Left: history rail */}
        <aside className="hidden w-[260px] shrink-0 flex-col border-r border-gray6 bg-gray7 md:flex">
          <div className="flex items-center gap-2 px-4 py-3.5 border-b border-gray6">
            <AgentOrb state={agentState} size={22} />
            <span className="text-[14px] font-bold text-black1">Tee Agent</span>
          </div>
          {renderHistory(true)}
        </aside>

        {/* Centre: conversation */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between border-b border-gray6 px-6 py-3.5">
            <div className="flex items-center gap-2">
              <img src={logoSvg} alt="TeeLogix" className="h-5 w-5" />
              <div className="leading-tight">
                <p className="text-[15px] font-bold text-black1">
                  {currentSession?.title || "New conversation"}
                </p>
                <p className="text-[11px] text-gray3">{statusLabel}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {screenContext?.pageName && (
                <span className="hidden items-center gap-1.5 rounded-full border border-green3/60 bg-green4 px-3 py-1 text-[11px] font-semibold text-green1 sm:flex">
                  <Eye className="h-3.5 w-3.5" />
                  Context: {screenContext.pageName}
                </span>
              )}
              {iconBtn("dock", <Minimize2 className="h-3.5 w-3.5" />, "Dock to side panel", () =>
                setLayoutMode("panel")
              )}
              {iconBtn(
                "close",
                <X className="h-3.5 w-3.5" />,
                "Close",
                toggleOpen,
                "hover:bg-red4 hover:text-red2"
              )}
            </div>
          </div>
          {renderConversation(true)}
          <SuggestionChips
            chips={suggestionChips}
            onSelect={(chip) => handleSendMessage(chipPromptMap[chip] || chip)}
            isLoading={isLoading}
          />
          {renderComposer(true)}
        </div>

        {/* Right: live page context */}
        <aside className="hidden w-[300px] shrink-0 flex-col gap-4 border-l border-gray6 bg-gray7 p-4 lg:flex">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray3">On this page</p>
          <div className="rounded-xl border border-gray6 bg-white p-3">
            <p className="text-[11px] text-gray3">Current screen</p>
            <p className="text-[15px] font-bold text-black1">{screenContext?.pageName || "—"}</p>
          </div>
          {pendingAction && (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray3">
                Pending action
              </p>
              <div className="flex items-center gap-2 rounded-xl border border-orange4 bg-orange4/60 px-3 py-2.5 text-[12px] font-semibold text-orange1">
                <Clock className="h-4 w-4" />1 awaiting confirmation
              </div>
            </>
          )}
          {screenContext?.chips && screenContext.chips.length > 0 && (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray3">Suggested</p>
              <div className="space-y-1.5">
                {screenContext.chips.slice(0, 4).map((chip) => (
                  <button
                    key={chip}
                    onClick={() => handleSendMessage(chip)}
                    className="w-full rounded-lg border border-gray5 bg-white px-3 py-2 text-left text-[12px] font-medium text-black1 hover:border-greentab"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </>
          )}
        </aside>
      </div>
    );
  }

  /* --------------------------------------------------------------------- */
  /* PANEL (right-side overlay drawer)                                      */
  /* --------------------------------------------------------------------- */
  return (
    <>
      {/* Dimming scrim behind an unpinned overlay */}
      {!isPinned && (
        <div
          onClick={() => setIsMinimized(true)}
          className="fixed inset-0 z-30 bg-black1/10 animate-in fade-in duration-200"
        />
      )}

      <div
        className={cn(
          "flex flex-col overflow-hidden bg-white z-40",
          isPinned
            ? "relative w-[380px] shrink-0 border-l border-gray6"
            : "fixed top-0 right-0 bottom-0 w-[380px] max-w-[85vw] shadow-[-8px_0_32px_rgba(32,41,54,0.18)] animate-in slide-in-from-right duration-300"
        )}
      >
        {/* Top bar */}
        <div className="px-4 py-3 flex items-center gap-2.5 border-b border-gray6 shrink-0">
          <AgentOrb state={agentState} size={26} />
          <div className="leading-tight flex-1 min-w-0">
            <div className="text-[13.5px] font-bold text-black1 flex items-center gap-1.5">
              <img src={logoSvg} alt="TeeLogix" className="h-4 w-4" />
              Tee Agent
            </div>
            <div className="text-[10.5px] text-gray3 truncate">{statusLabel}</div>
          </div>

          {iconBtn(
            "history",
            <History className="h-3.5 w-3.5" />,
            showHistory ? "Back to chat" : "History",
            () => setShowHistory(!showHistory),
            showHistory ? "bg-green4 text-green2" : undefined
          )}
          {iconBtn(
            "full",
            <Maximize2 className="h-3.5 w-3.5" />,
            "Full screen",
            toggleLayoutMode
          )}
          {iconBtn(
            "pin",
            isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />,
            isPinned ? "Unpin" : "Pin",
            togglePinned
          )}
          {iconBtn("reset", <RotateCcw className="h-3.5 w-3.5" />, "New conversation", clearChat)}
          {iconBtn(
            "collapse",
            <PanelRightClose className="h-3.5 w-3.5" />,
            "Collapse",
            () => setIsMinimized(true)
          )}
          {iconBtn(
            "close",
            <X className="h-3.5 w-3.5" />,
            "Close",
            toggleOpen,
            "hover:bg-red4 hover:text-red2"
          )}
        </div>

        {showHistory ? (
          <>
            <div className="flex items-center gap-2 px-4 pb-1 pt-2">
              <button
                onClick={() => setShowHistory(false)}
                className="flex items-center gap-1 text-[12px] font-semibold text-green2"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to chat
              </button>
            </div>
            {renderHistory()}
          </>
        ) : (
          <>
            <ContextBanner />
            {renderConversation()}
            <SuggestionChips
              chips={suggestionChips}
              onSelect={(chip) => handleSendMessage(chipPromptMap[chip] || chip)}
              isLoading={isLoading}
            />
            {renderComposer()}
          </>
        )}
      </div>
    </>
  );
};
